// src/components/compartidos/Header.js
import React, { useState, useRef, useEffect } from 'react';
import './Header.css';
import logoWisby from '../../assets/logo.png';
import ProfileMenu from '../cuenta/ProfileMenu';
import { guardAdminNavigation } from '../admin/UnsavedChanges';

// Componente `ProfileIconSVG` que renderiza esta parte de la interfaz.
const ProfileIconSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

// Componente `Header` que renderiza esta parte de la interfaz.
export default function Header({
  currentUser,
  userRole,
  isAdmin,
  onSetActiveSection,
  onToggleMenu,
  currentLanguage,
  translations,
  onLogout,
  onRoleChanged,
  onSetProfileTab,
  isLanding = false,
  isWorkspace = false,
  notifications,
  t = {}
}) {
  
  // Estado local `isProfileMenuOpen` y su actualizador, usados para reflejar cambios en la interfaz.
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  // Estado local `isServicesMenuOpen` y su actualizador, usados para reflejar cambios en la interfaz.
  const [isServicesMenuOpen, setIsServicesMenuOpen] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState('');
  const [rolesFetchVersion, setRolesFetchVersion] = useState(0);
  const [roleSwitchBusy, setRoleSwitchBusy] = useState(false);
  const [roleSwitchError, setRoleSwitchError] = useState('');
  const notaryRoles = ['notario', 'admin_notaria', 'abogado_notaria'];
  const isStaffAccount = ['admin', 'abogado_wisby', ...notaryRoles].includes(userRole);
  const staffHome = userRole === 'admin' ? 'admin'
    : userRole === 'abogado_wisby' ? 'folios-abogado'
      : notaryRoles.includes(userRole) ? 'notario-dashboard' : 'inicio';
  // Los roles disponibles siempre provienen de la cuenta autenticada en el servidor.
  // El cliente no decide qué permisos conceder ni permite seleccionar roles no asignados.
  useEffect(() => {
    if (!currentUser) {
      setAvailableRoles([]);
      setRolesError('');
      setRoleSwitchError('');
      return;
    }
    if (!isProfileMenuOpen) return;
    const controller = new AbortController();
    const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
    const token = storage.getItem('token');
    if (!token) {
      setRolesLoading(false);
      setRolesError('Tu sesión ya no está disponible. Inicia sesión nuevamente.');
      return;
    }
    setRolesLoading(true);
    setRolesError('');
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/roles/me`, {
      headers: { 'x-auth-token': token },
      signal: controller.signal,
    })
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.msg || 'No se pudieron cargar tus espacios de trabajo.');
        return data;
      })
      .then(data => {
        if (controller.signal.aborted) return;
        const accepted = ['user', 'admin', 'abogado_wisby', 'notario', 'admin_notaria', 'abogado_notaria'];
        setAvailableRoles([...new Set((Array.isArray(data.roles) ? data.roles : []).filter(role => accepted.includes(role)))]);
      })
      .catch(error => {
        if (!controller.signal.aborted) {
          setAvailableRoles([]);
          setRolesError(error.message || 'No se pudieron consultar tus roles.');
        }
      })
      .finally(() => { if (!controller.signal.aborted) setRolesLoading(false); });
    return () => controller.abort();
  }, [currentUser, userRole, isProfileMenuOpen, rolesFetchVersion]);

  const roleHome = role => role === 'admin' ? 'admin'
    : role === 'abogado_wisby' ? 'folios-abogado'
      : ['notario', 'admin_notaria', 'abogado_notaria'].includes(role) ? 'notario-dashboard' : 'inicio';

  const switchWorkspace = nextRole => {
    if (roleSwitchBusy || rolesLoading || !availableRoles.includes(nextRole)) return;
    if (nextRole === userRole) {
      setIsProfileMenuOpen(false);
      onSetActiveSection(roleHome(nextRole));
      return;
    }
    // Antes de cambiar de función, respetar los cambios no guardados del expediente.
    guardAdminNavigation(async () => {
      setRoleSwitchBusy(true);
      setRoleSwitchError('');
      try {
        const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
        const token = storage.getItem('token');
        if (!token) throw new Error('Tu sesión ha expirado. Inicia sesión nuevamente.');
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/roles/active`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
          body: JSON.stringify({ role: nextRole }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.msg || 'No se pudo cambiar de espacio de trabajo.');
        if (data.role !== nextRole) throw new Error('El servidor no confirmó el rol seleccionado.');
        storage.setItem('userRole', data.role);
        setIsProfileMenuOpen(false);
        setAvailableRoles([]);
        if (onRoleChanged) onRoleChanged(data.role);
        else window.location.assign('/');
      } catch (error) {
        setRoleSwitchError(error.message || 'No se pudo cambiar de espacio de trabajo.');
      } finally {
        setRoleSwitchBusy(false);
      }
    });
  };
  // Referencia mutable `menuRef` que conserva datos entre renderizados sin provocar uno nuevo.
  const menuRef = useRef(null);
  // Referencia mutable `servicesRef` que conserva datos entre renderizados sin provocar uno nuevo.
  const servicesRef = useRef(null);

  useEffect(() => {
    // Función `handleClickOutside` que gestiona la interacción o el evento correspondiente.
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef]);

  // Función `handleNavigateFromMenu` que gestiona la interacción o el evento correspondiente.
  const handleNavigateFromMenu = (section, tab) => {
    onSetActiveSection(section, tab);
    setIsProfileMenuOpen(false);
  };

  // Función `handleLogoutFromMenu` que gestiona la interacción o el evento correspondiente.
  const handleLogoutFromMenu = () => {
    if (onLogout) onLogout(); 
    setIsProfileMenuOpen(false);
  };

  // Función `handleServiceSelect` que gestiona la interacción o el evento correspondiente.
  const handleServiceSelect = (section) => {
    if (currentUser) {
      onSetActiveSection(section);
    } else {
      onSetActiveSection('registrate');
    }
    setIsServicesMenuOpen(false);
  };

  return (
    <header className={`header-wisby${isLanding ? ' header-wisby--landing' : ''}${isWorkspace ? ' header-wisby--workspace' : ''}`}>
      <div className="header-content">
        <img 
          src={logoWisby} 
          alt="Wisby Logo" 
          className="logo-wisby" 
          onClick={() => onSetActiveSection(isStaffAccount ? staffHome : 'inicio')} 
        />
        
        <nav className="nav-links-wisby">
          <button onClick={() => onSetActiveSection('nosotros')} className="nav-button-wisby">
            {t.nosotros || "Nosotros"} 
          </button>
          
          {!isStaffAccount && <div className="services-dropdown" ref={servicesRef}>
            <button 
              onClick={() => setIsServicesMenuOpen(!isServicesMenuOpen)} 
              className="nav-button-wisby services-trigger"
            >
              {t.servicios || "Servicios"}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, transition: 'transform 0.2s', transform: isServicesMenuOpen ? 'rotate(180deg)' : 'rotate(0)' }}>
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            {isServicesMenuOpen && (
              <div className="services-dropdown-menu">
                <button onClick={() => handleServiceSelect('crear-accion')} className="services-dropdown-item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                  </svg>
                  <div>
                    <span className="services-item-title">Acciones</span>
                    <span className="services-item-desc">Títulos accionarios corporativos</span>
                  </div>
                </button>
                <button onClick={() => handleServiceSelect('crear-poder')} className="services-dropdown-item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <path d="M12 18v-6"></path>
                    <path d="M9 15l3 3 3-3"></path>
                  </svg>
                  <div>
                    <span className="services-item-title">Poderes</span>
                    <span className="services-item-desc">Otorgamiento de poderes legales</span>
                  </div>
                </button>
              </div>
            )}
          </div>}
          
          {notifications}

          {currentUser ? (
            <div className="profile-container" ref={menuRef} style={{position: 'relative'}}>
              <div 
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} 
                className={`profile-icon ${isAdmin ? 'is-admin' : ''}`}
                role="button"
                tabIndex={0}
                aria-label="Menú de cuenta"
                aria-expanded={isProfileMenuOpen}
                onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setIsProfileMenuOpen(value => !value); } }}
                title={isAdmin ? 'Mi perfil (Admin)' : userRole === 'notario' ? 'Mi perfil (Notario)' : userRole === 'abogado_wisby' ? 'Mi perfil (Abogado - Wisby)' : 'Mi perfil'}
              >
                <ProfileIconSVG />
                {/* Indicador visual de admin */}
                {(isAdmin || ['notario', 'abogado_wisby'].includes(userRole)) && <span className={`admin-indicator${userRole === 'notario' ? ' notario-indicator' : userRole === 'abogado_wisby' ? ' lawyer-indicator' : ''}`}></span>}
              </div>

              {/* Renderizamos el menú si el estado es true */}
              {isProfileMenuOpen && (
                <ProfileMenu 
                  userName={currentUser}
                  userRole={userRole}
                  onNavigate={handleNavigateFromMenu}
                  onLogout={handleLogoutFromMenu}
                  isAdmin={isAdmin}
                  availableRoles={availableRoles}
                  rolesLoading={rolesLoading}
                  rolesError={rolesError}
                  roleSwitchBusy={roleSwitchBusy}
                  roleSwitchError={roleSwitchError}
                  onRoleSwitch={switchWorkspace}
                  onRetryRoles={() => setRolesFetchVersion(value => value + 1)}
                  t={t}
                />
              )}
            </div>
          ) : (
            <button 
              onClick={() => {
                onSetActiveSection('auth');
                window.dispatchEvent(new Event('wisby:open-login'));
              }}
              className="nav-button-wisby action-button"
            >
              {t.iniciarSesion || "Iniciar Sesión"}
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}
